import styled, {css} from "styled-components";
import {SiteLink as RouterLink} from "nav/SiteLink";
import {fontFamilies, fontWeights, radii} from "theme/tokens";
import {eyebrowTextCss} from "theme/typography";

/**
 * Externalized styles for {@link ./AdvertisingContent} — the landing page's shared furniture and the
 * three bands that file owns (the terms strip, the four steps, and the two short lists).
 *
 * Colours are `theme.tokens` reads, so light and dark fall out of the token layer. The call to action's
 * geometry matches `SponsorPage.styles`'s primary button, because this control and the booking page's
 * own submit are the same action seen a page apart.
 */

/**
 * The horizontal measure every band lines up on.
 *
 * 1160px since ui#1861, up from the 760px prose measure the page inherited from `ContentSurface`. The
 * page stopped being an article: it is now a hero beside a mock, a four-across step row and a
 * three-across gallery, and none of those fit in a column sized for running text. The RUNNING text is
 * still capped, but per element ({@link SectionLede}, {@link ListItem}) rather than by the page, which
 * is what lets a paragraph read at 36em inside a band that is 1160px wide.
 *
 * Exported rather than duplicated: {@link ./AdvertisingHero.styles}, {@link ./AdvertisingTreatments.styles}
 * and {@link ./AdvertisingCloser.styles} all read the SAME value, which is what makes the eyebrow, the
 * display headline, the steps and the closing band start on one left edge instead of five that agree
 * until somebody edits one of them.
 */
export const advertisingMeasure = css`
	width: 100%;
	max-width: 1160px;
	margin: 0 auto;
	box-sizing: border-box;
	padding: 0 24px;

	@media (max-width: 640px) {
		padding: 0 18px;
	}
`;

/**
 * The page's `<main>` landmark.
 *
 * Full width, unlike the shared `ContentSurface` it replaced: the hero and the closing band are
 * full-bleed, and a surface that caps its children at the prose measure cannot hold either.
 */
export const PageRoot = styled.main`
	position: relative;
	z-index: 1;
	width: 100%;
	text-align: left;
	color: ${({theme}) => theme.tokens.ink2};
	font-family: ${fontFamilies.sans};
	background: ${({theme}) => theme.tokens.paper};
`;

/**
 * "Start advertising" — a real anchor, never a button with an onClick.
 *
 * On the page it is a normal link a visitor can middle-click or copy, and it is still the control every
 * other surface resolves the hand-off through. Kept exported and shared by all three mounts (the hero,
 * the closing band, and any future one) so a second, differently built button cannot be the one that
 * quietly stops working.
 *
 * `&&` is retained deliberately even though ui#1861 removed the `Prose` wrapper this page used to
 * render inside. It costs one class of specificity and nothing else, and it is what kept the label
 * readable the last time an ancestor's `a { color: blue }` descendant rule outranked a single-class
 * button rule and painted brand blue on a brand-blue button. The anchor's own colours are stated rather
 * than inherited for the same reason.
 */
export const StartAdvertisingLink = styled(RouterLink)`
	&& {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: 48px;
		padding: 0 24px;
		border-radius: ${radii.control};
		font-family: ${fontFamilies.sans};
		font-size: 15px;
		font-weight: 700;
		text-decoration: none;
		background: ${({theme}) => theme.tokens.blue};
		color: ${({theme}) => theme.tokens.blueInk};
		transition: background 0.16s;

		&:hover {
			background: ${({theme}) => theme.tokens.blued};
		}

		&:focus-visible {
			outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
			outline-offset: 2px;
		}

		@media (max-width: 600px) {
			width: 100%;
		}
	}
`;

/**
 * The quieter second route to the same page: a text link, not a second button.
 *
 * It goes to the SAME destination as the button beside it. That is on purpose rather than redundant —
 * it names what a reader who is not ready to "start" actually wants (the calendar and the rate), and
 * the rate is the one number this page deliberately does not answer itself.
 */
export const SecondaryLink = styled(RouterLink)`
	&& {
		font-family: ${fontFamilies.sans};
		font-size: 15px;
		font-weight: 600;
		text-decoration: none;
		color: ${({theme}) => theme.tokens.blue};

		&:hover {
			text-decoration: underline;
		}

		&:focus-visible {
			outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
			outline-offset: 3px;
			border-radius: 4px;
		}
	}
`;

/** Wraps a call to action so the control and whatever sits beside it read as one block. */
export const CtaRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 12px 18px;
	margin: 28px 0 0;
`;

/** The one-line reassurance beside the button: what the next page does before it asks for anything. */
export const CtaNote = styled.span`
	font-size: 14px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** A content band between the hero and the closer. */
export const SectionBand = styled.section`
	${advertisingMeasure};
	padding: 72px 0 0;

	@media (max-width: 640px) {
		padding-top: 52px;
	}
`;

/** Heading and its one-paragraph lede, capped short of the band so the pair reads as a unit. */
export const SectionHead = styled.div`
	max-width: 40em;
`;

/**
 * A band heading.
 *
 * Sized between the hero's display type and the body: loud enough to break the page into chapters, not
 * so loud that it competes with the `<h1>` that opens it.
 */
export const SectionHeading = styled.h2`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: clamp(28px, 3.6vw, 44px);
	line-height: 1.08;
	letter-spacing: -0.015em;
	color: ${({theme}) => theme.tokens.ink};
`;

export const SectionLede = styled.p`
	margin: 12px 0 0;
	max-width: 36em;
	font-size: 16px;
	line-height: 1.6;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The terms strip: ruled cells stating what a booking is, directly under the hero.
 *
 * Ruled rather than carded because these are FACTS about one offer, not three things to choose between
 * — a row of cards would read as a pricing table with nothing to pick.
 *
 * The column count is `auto-fit` rather than a fixed three. The strip renders two cells when the
 * availability read has not returned or failed, and three when it has, so a `repeat(3, 1fr)` would
 * leave a hole where the run-length cell will be — the exact layout jump the read is deferred to avoid
 * causing. With `auto-fit` the two static cells simply share the width until the third joins them.
 */
export const TermsStrip = styled.section`
	${advertisingMeasure};
	margin-top: 44px;

	@media (max-width: 640px) {
		margin-top: 32px;
	}
`;

export const TermsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
	border-top: 1px solid ${({theme}) => theme.tokens.line};
	border-bottom: 1px solid ${({theme}) => theme.tokens.line};
`;

/**
 * One cell.
 *
 * The separator is a `border-left` on every cell but the first, so it never has to be cancelled on a
 * last child whose position changes with the column count — at two columns the "last" cell is the
 * second on one row and the third on the next, and a `:last-child` rule would rule the wrong one.
 */
export const TermsCell = styled.div`
	padding: 22px 20px;
	box-sizing: border-box;

	& + & {
		border-left: 1px solid ${({theme}) => theme.tokens.line};
	}

	&:first-child {
		padding-left: 0;
	}

	/* Stacked, the vertical rule is meaningless and the horizontal one is what separates them. */
	@media (max-width: 560px) {
		padding: 18px 0;

		& + & {
			border-left: 0;
			border-top: 1px solid ${({theme}) => theme.tokens.line};
		}
	}
`;

/** The figure. Serif and tabular, so the numbers line up and read as quantities rather than as copy. */
export const TermsFigure = styled.p`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: 36px;
	line-height: 1;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink};
`;

/** What the figure counts, set beside it in the body face so the number stays the loud part. */
export const TermsUnit = styled.span`
	/* Small, because the markup already writes a real space between the figure and this element. */
	margin-left: 2px;
	font-family: ${fontFamilies.sans};
	font-weight: 400;
	font-size: 14px;
	color: ${({theme}) => theme.tokens.ink2};
`;

export const TermsCaption = styled.p`
	margin: 8px 0 0;
	font-size: 13.5px;
	line-height: 1.5;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * Marks a figure that came from the live availability read rather than from the product.
 *
 * Worth saying out loud: "1 advertiser per day" and "6 treatments" are true of the product and will be
 * true tomorrow, while the run length is operator policy that can change between two visits. A reader
 * who cannot tell the two apart has no way to know which of the three to trust in a screenshot.
 */
export const TermsLiveTag = styled.span`
	${eyebrowTextCss};
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-top: 8px;
	font-size: 10.5px;
	color: ${({theme}) => theme.tokens.ink2};

	&::before {
		content: "";
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: ${({theme}) => theme.tokens.teal};
	}
`;

/**
 * The four booking steps.
 *
 * An ordered list in the markup, because they ARE ordered — this is the booking page's own sequence, and
 * a screen reader should hear "list of 4 items" rather than four unrelated headings.
 */
export const StepsList = styled.ol`
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 28px;
	margin: 36px 0 0;
	padding: 0;
	list-style: none;

	@media (max-width: 900px) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	@media (max-width: 520px) {
		grid-template-columns: 1fr;
	}
`;

export const StepItem = styled.li`
	display: grid;
	gap: 10px;
	align-content: start;
`;

/** The illustration's frame. A fixed height so four uneven drawings sit on one baseline. */
export const StepArtFrame = styled.div`
	display: grid;
	place-items: center;
	height: 120px;
	border-radius: ${radii.panel};
	background: ${({theme}) => theme.tokens.card2};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
`;

export const StepNumber = styled.span`
	${eyebrowTextCss};
	text-transform: none;
	color: ${({theme}) => theme.tokens.blue};
`;

export const StepTitle = styled.h3`
	margin: 0;
	font-size: 18px;
	font-weight: 700;
	line-height: 1.3;
	color: ${({theme}) => theme.tokens.ink};
`;

export const StepLine = styled.p`
	margin: 0;
	font-size: 14.5px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/** The two short lists, side by side. */
export const ListPair = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 48px;
	margin-top: 36px;

	@media (max-width: 760px) {
		grid-template-columns: 1fr;
		gap: 40px;
	}
`;

/**
 * A list's heading.
 *
 * An `<h2>`, like every other band heading, and set smaller than the rest on purpose: this band has two
 * of them side by side, so a pair at the section scale would read as two separate chapters rather than
 * as one comparison. The band therefore carries no heading of its own — these two ARE its content.
 */
export const ListHeading = styled.h2`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: ${fontWeights.display};
	font-size: 28px;
	letter-spacing: -0.01em;
	line-height: 1.15;
	color: ${({theme}) => theme.tokens.ink};
`;

export const PointList = styled.ul`
	display: grid;
	gap: 14px;
	margin: 18px 0 0;
	padding: 0;
	list-style: none;
`;

export const ListItem = styled.li`
	display: grid;
	grid-template-columns: 28px 1fr;
	gap: 12px;
	align-items: start;
	max-width: 34em;
	font-size: 15px;
	line-height: 1.55;
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * The glyph beside a point.
 *
 * Two tints, keyed by which list it is in: the blue accent tint for the audience list, the green signal
 * tint for the promises. The distinction is not decorative — a promise is a commitment and a fact is
 * not, and the eye should be able to tell the two columns apart without reading either heading.
 */
export const ListGlyph = styled.i<{$signal: boolean}>`
	display: grid;
	place-items: center;
	width: 28px;
	height: 28px;
	border-radius: ${radii.chip};
	background: ${({theme, $signal}) => ($signal ? theme.tokens.signalTint : theme.tokens.accentTint)};
	color: ${({theme, $signal}) => ($signal ? theme.tokens.teal : theme.tokens.blue)};
`;

/** The bolded lead of a point, on its own line above the rest of the sentence. */
export const PointLead = styled.strong`
	display: block;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink};
`;
